/**
 * Ringtones as Web Audio patterns. No audio files to ship, no autoplay
 * policy pain because the AudioContext is created lazily on demand.
 *
 * Preference is stored per-device in localStorage under `oc.ringtone`.
 */

export type RingtoneId = 'porch-bell' | 'chime' | 'cove-call' | 'silent';

interface Ringtone {
  id: RingtoneId;
  name: string;
  description: string;
  /** period in ms between phrases while ringing */
  phraseMs: number;
  /** play one phrase; returns when the phrase's tail has decayed */
  play(ctx: AudioContext, out: AudioNode): void;
}

const STORAGE_KEY = 'oc.ringtone';
const DEFAULT: RingtoneId = 'porch-bell';

function ping(
  ctx: AudioContext,
  out: AudioNode,
  freq: number,
  startAt: number,
  durSec: number,
  type: OscillatorType = 'sine',
  peak = 0.35,
): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startAt);
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(peak, startAt + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durSec);
  osc.connect(gain).connect(out);
  osc.start(startAt);
  osc.stop(startAt + durSec + 0.02);
}

const PORCH_BELL: Ringtone = {
  id: 'porch-bell',
  name: 'Porch bell',
  description: 'A slow brass bell, two strikes',
  phraseMs: 2600,
  play(ctx, out) {
    const t = ctx.currentTime;
    // A brass-bell-like sound: fundamental + third + fifth, all decaying.
    for (const [when, freq, peak] of [
      [0.00, 587.33, 0.30], // D5
      [0.00, 880.00, 0.15], // A5 (fifth)
      [0.00, 1174.66, 0.08], // D6 (octave)
      [0.75, 587.33, 0.24],
      [0.75, 880.00, 0.12],
      [0.75, 1174.66, 0.06],
    ] as const) {
      ping(ctx, out, freq, t + when, 1.6, 'sine', peak);
    }
  },
};

const CHIME: Ringtone = {
  id: 'chime',
  name: 'Chime',
  description: 'A rising triangle-wave phrase',
  phraseMs: 2200,
  play(ctx, out) {
    const t = ctx.currentTime;
    const notes: [number, number][] = [
      [0.00, 659.25], // E5
      [0.15, 783.99], // G5
      [0.30, 987.77], // B5
      [0.45, 1318.51], // E6
    ];
    for (const [when, freq] of notes) {
      ping(ctx, out, freq, t + when, 0.55, 'triangle', 0.25);
    }
  },
};

const COVE_CALL: Ringtone = {
  id: 'cove-call',
  name: 'Cove call',
  description: 'A gentle two-note beacon',
  phraseMs: 1800,
  play(ctx, out) {
    const t = ctx.currentTime;
    ping(ctx, out, 523.25, t + 0.00, 0.55, 'sine', 0.28); // C5
    ping(ctx, out, 392.00, t + 0.35, 0.75, 'sine', 0.24); // G4
  },
};

const SILENT: Ringtone = {
  id: 'silent',
  name: 'Silent',
  description: 'Vibration only (if the device supports it)',
  phraseMs: 2000,
  play() {
    /* no-op */
  },
};

export const RINGTONES: Ringtone[] = [PORCH_BELL, CHIME, COVE_CALL, SILENT];

export function getRingtoneId(): RingtoneId {
  const v = (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY)) as RingtoneId | null;
  return RINGTONES.some((r) => r.id === v) ? (v as RingtoneId) : DEFAULT;
}

export function setRingtoneId(id: RingtoneId): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

export function getRingtone(id: RingtoneId): Ringtone {
  return RINGTONES.find((r) => r.id === id) ?? PORCH_BELL;
}

let sharedCtx: AudioContext | null = null;
function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const AC = (window.AudioContext ?? (window as any).webkitAudioContext) as typeof AudioContext | undefined;
  if (!AC) return null;
  if (!sharedCtx) sharedCtx = new AC();
  // Autoplay policy: resume if suspended (browsers pause new contexts).
  if (sharedCtx.state === 'suspended') void sharedCtx.resume();
  return sharedCtx;
}

/**
 * Play one phrase of the given ringtone. Useful for the settings picker preview.
 * Returns a promise that resolves after the phrase length so callers can await it.
 */
export async function playPreview(id: RingtoneId): Promise<void> {
  const ctx = getCtx();
  if (!ctx) return;
  const master = ctx.createGain();
  master.gain.value = 0.9;
  master.connect(ctx.destination);
  getRingtone(id).play(ctx, master);
  await new Promise((r) => setTimeout(r, getRingtone(id).phraseMs));
}

/**
 * Start ringing (looping the phrase). Returns a stop function.
 * If the device supports vibration, also vibrate in a matching pattern.
 */
export function startRinging(id?: RingtoneId): () => void {
  const chosen = getRingtone(id ?? getRingtoneId());
  const ctx = getCtx();
  let stopped = false;
  let vibrateTimer: number | null = null;
  let phraseTimer: number | null = null;

  function tick(): void {
    if (stopped) return;
    if (ctx) {
      const master = ctx.createGain();
      master.gain.value = 0.9;
      master.connect(ctx.destination);
      chosen.play(ctx, master);
    }
    phraseTimer = window.setTimeout(tick, chosen.phraseMs);
  }

  if ('vibrate' in navigator) {
    const doVibrate = (): void => {
      if (stopped) return;
      try { navigator.vibrate([300, 200, 300, 900]); } catch {}
      vibrateTimer = window.setTimeout(doVibrate, chosen.phraseMs);
    };
    doVibrate();
  }
  tick();

  return () => {
    stopped = true;
    if (phraseTimer !== null) clearTimeout(phraseTimer);
    if (vibrateTimer !== null) clearTimeout(vibrateTimer);
    if ('vibrate' in navigator) { try { navigator.vibrate(0); } catch {} }
  };
}
